import { createContext, useContext, useEffect, useState, type ReactNode } from "react"

import { isEmbeddedChatMessage } from "@/types/embedded-chat.types"

type Theme = "light" | "dark" | "system"

type ThemeProviderContext = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderContext = {
  theme: "system",
  setTheme: () => null
}

const ThemeProviderContext = createContext<ThemeProviderContext>(initialState)

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

export const ThemeProvider = ({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme
    }
    return defaultTheme
  })

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    try {
      const root = window.document.documentElement

      root.classList.remove("dark", "light")

      if (theme === "system") {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
        root.classList.add(systemTheme)
        root.style.colorScheme = systemTheme
        return
      }

      root.classList.add(theme)
      root.style.colorScheme = theme
    } catch (error) {
      console.error("Error applying theme:", error)
    }
  }, [theme])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isEmbeddedChatMessage(event.data, "THEME")) return
      if (localStorage.getItem(storageKey)) return

      setTheme(event.data.value.theme)
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [storageKey])

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme)
      setTheme(newTheme)
    }
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}
